// Rotas falsas de Notifications (templates, versões, aprovação-façade, envio de teste, preferências — M1 §6.3/§7.2).
import type { MeResponse } from '@giyus/api-client';

import type {
  MessageTemplateSummary,
  TestSendRequest,
} from '../../modules/notifications/api/contract.preview';
import { FakeApiError, jsonResponse, readJsonBody } from './http';
import {
  type FakeNotificationsState,
  type FakeTemplate,
  type FakeTemplateVersion,
  defaultPreferences,
} from './notificationsStore';
import type { FakeApproval, FakeWorkflowState } from './workflowStore';
import { callerHasRole, nextId } from './workflowStore';

const PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

function summarize(template: FakeTemplate): MessageTemplateSummary {
  const current =
    [...template.versions].reverse().find((v) => v.status === 'Approved') ??
    template.versions[template.versions.length - 1];
  return {
    id: template.id,
    key: template.key,
    channels: template.channels,
    audience: template.audience,
    sender: template.sender,
    mandatory: template.mandatory,
    currentVersionStatus: current?.status ?? 'Draft',
    currentVersion: current?.version ?? 0,
  };
}

function validateBody(
  locale: FakeTemplateVersion['locales']['pt-BR'],
  mergeFields: readonly { name: string }[],
): void {
  const declared = new Set(mergeFields.map((f) => f.name));
  for (const field of [locale.subject, locale.bodyHtml, locale.bodyText ?? '']) {
    if (field.includes('{{{')) {
      throw new FakeApiError(422, 'NTF_TEMPLATE_SYNTAX_INVALID', {
        detail: 'Triple braces are not allowed.',
      });
    }
    for (const match of field.matchAll(PLACEHOLDER)) {
      const name = match[1];
      if (name && !declared.has(name)) {
        throw new FakeApiError(422, 'NTF_TEMPLATE_SYNTAX_INVALID', {
          detail: `Unknown placeholder: ${name}`,
        });
      }
    }
  }
}

export interface NotificationsRouteContext {
  readonly ntf: FakeNotificationsState;
  readonly wf: FakeWorkflowState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
}

function findApproval(wf: FakeWorkflowState, id: string | null): FakeApproval | undefined {
  return id ? wf.approvals.find((a) => a.id === id) : undefined;
}

function approveTemplateVersion(template: FakeTemplate, version: FakeTemplateVersion): void {
  for (const other of template.versions) {
    if (other.status === 'Approved' && other !== version) {
      other.status = 'Retired';
    }
  }
  version.status = 'Approved';
  version.approvalRequestId = null;
  template.pendingApprovalId = null;
}

export async function handleNotificationsRoute(
  request: Request,
  segments: readonly string[],
  ctx: NotificationsRouteContext,
): Promise<Response | null> {
  const { ntf, wf, me, correlationId } = ctx;
  const method = request.method.toUpperCase();

  try {
    if (segments[0] === 'me' && segments[1] === 'notification-preferences') {
      const existing = ntf.preferencesByUserId.get(me.user.id) ?? defaultPreferences();
      if (method === 'GET') {
        return jsonResponse(existing);
      }
      if (method === 'PUT') {
        const body = (await readJsonBody(request)) as Partial<
          Omit<typeof existing, 'etag' | 'smsAvailable' | 'mandatoryCategories'>
        >;
        const updated = {
          ...existing,
          ...body,
          smsAvailable: existing.smsAvailable,
          mandatoryCategories: existing.mandatoryCategories,
          etag: `W/"prefs-${String(Date.now())}"`,
        };
        ntf.preferencesByUserId.set(me.user.id, updated);
        return jsonResponse(updated);
      }
    }

    if (segments[0] !== 'network' || segments[1] !== 'notification-templates') {
      return null;
    }
    if (!me.networkAccess.hasAccess) {
      throw new FakeApiError(403, 'PLT_NETWORK_SCOPE_REQUIRED');
    }
    if (segments.length === 2 && method === 'GET') {
      return jsonResponse({ items: ntf.templates.map(summarize) });
    }
    const templateId = segments[2];
    const template = templateId ? ntf.templates.find((t) => t.id === templateId) : undefined;
    if (!template) {
      return problemNotFound(correlationId);
    }
    if (segments.length === 3 && method === 'GET') {
      return jsonResponse({ ...summarize(template), versions: template.versions });
    }
    if (segments[3] === 'versions' && segments.length === 4 && method === 'GET') {
      return jsonResponse({ items: template.versions });
    }
    if (segments[3] === 'versions' && segments[4]) {
      const versionNumber = Number(segments[4]);
      const version = template.versions.find((v) => v.version === versionNumber);
      if (!version) {
        return problemNotFound(correlationId);
      }
      if (segments.length === 5 && method === 'GET') {
        return jsonResponse(version);
      }
      if (segments.length === 5 && method === 'PUT') {
        if (version.status !== 'Draft') {
          throw new FakeApiError(409, 'NTF_TEMPLATE_VERSION_IN_REVIEW');
        }
        const body = (await readJsonBody(request)) as {
          locales?: FakeTemplateVersion['locales'];
          changeReason?: string;
        };
        if (body.locales) {
          validateBody(body.locales['pt-BR'], version.mergeFields);
          validateBody(body.locales['en-US'], version.mergeFields);
          version.locales = body.locales;
        }
        version.changeReason = body.changeReason ?? version.changeReason;
        version.etag = `W/"tpl-${template.id}-v${String(version.version)}-${String(Date.now())}"`;
        return jsonResponse(version);
      }
      if (segments[5] === 'preview' && method === 'POST') {
        return jsonResponse({
          emailHtml: `<div>${version.locales['pt-BR'].bodyHtml}</div>`,
          text:
            version.locales['pt-BR'].bodyText ??
            version.locales['pt-BR'].bodyHtml.replace(/<[^>]+>/g, ''),
        });
      }
    }
    if (segments[3] === 'submit' && method === 'POST') {
      const draft = [...template.versions].reverse().find((v) => v.status === 'Draft');
      if (!draft) {
        throw new FakeApiError(409, 'WF_INVALID_TRANSITION');
      }
      const actType =
        template.audience === 'Participant'
          ? 'ntf.template-version.participant'
          : 'ntf.template-version.internal';
      const approval: FakeApproval = {
        id: nextId('appr'),
        tenantId: null,
        studySiteId: null,
        actType,
        objectType: 'MessageTemplate',
        objectId: template.key,
        objectVersion: `v${String(draft.version)}`,
        objectSummary: `${template.key} v${String(draft.version)} — ${draft.changeReason ?? 'sem razão informada'}`,
        objectContentHash: draft.objectContentHash,
        requestedByUserId: me.user.id,
        requestedAt: new Date().toISOString(),
        dueAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
        currentStep: 1,
        // Simplificação do modo fake — ver TODO(GIY-025) em workflowStore.seedWorkflowState.
        steps: [
          {
            order: 1,
            approverRoleCodes: ['HOLDING_ADMIN'],
            approverScope: 'Network',
            requiredPermission: 'notification.template.approve',
          },
        ],
        status: 'Pending',
        requiresSignature: true,
        decisions: [],
        closedAt: null,
      };
      wf.approvals.push(approval);
      draft.status = 'InReview';
      draft.approvalRequestId = approval.id;
      template.pendingApprovalId = approval.id;
      return jsonResponse(draft);
    }
    if (segments[3] === 'approve' && method === 'POST') {
      const approval = findApproval(wf, template.pendingApprovalId);
      const draft = [...template.versions].reverse().find((v) => v.status === 'InReview');
      if (!approval || !draft) {
        throw new FakeApiError(409, 'WF_INVALID_TRANSITION');
      }
      if (approval.requestedByUserId === me.user.id) {
        throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
      }
      const step = approval.steps.find((s) => s.order === approval.currentStep);
      if (!step?.approverRoleCodes.some((role) => callerHasRole(me, role, null))) {
        throw new FakeApiError(422, 'WF_APPROVER_NOT_ELIGIBLE');
      }
      const body = (await readJsonBody(request)) as { meaning?: string; comment?: string };
      approval.status = 'Approved';
      approval.closedAt = new Date().toISOString();
      approval.decisions.push({
        id: nextId('decision'),
        step: approval.currentStep,
        deciderUserId: me.user.id,
        deciderName: me.user.displayName,
        decision: 'Approved',
        meaning: body.meaning ?? 'Approved',
        comment: body.comment ?? null,
        decidedAt: new Date().toISOString(),
      });
      approveTemplateVersion(template, draft);
      return jsonResponse(draft);
    }
    if (segments[3] === 'retire' && method === 'POST') {
      const approval = findApproval(wf, template.pendingApprovalId);
      if (approval?.status === 'Pending') {
        approval.status = 'Cancelled';
        approval.closedAt = new Date().toISOString();
      }
      const draft = [...template.versions].reverse().find((v) => v.status === 'InReview');
      if (draft) {
        draft.status = 'Draft';
        draft.approvalRequestId = null;
      }
      template.pendingApprovalId = null;
      return jsonResponse(summarize(template));
    }
    if (segments[3] === 'test-send' && method === 'POST') {
      const rawBody = await readJsonBody(request);
      // Valida a forma de `recipient` antes de fazer `as TestSendRequest`: sem isto, um corpo sem
      // `recipient` (ou com um valor que não seja objeto) faz `'address' in body.recipient` estourar
      // `TypeError` — exceção que escapa deste `try/catch` (só trata `FakeApiError`) e derruba a chamada
      // em vez de responder um problem+json 422, como todo outro erro de validação da rota.
      const recipient = (rawBody as { recipient?: unknown } | null)?.recipient;
      if (typeof recipient !== 'object' || recipient === null) {
        throw new FakeApiError(422, 'PLT_VALIDATION', {
          errors: [{ field: 'recipient', code: 'REQUIRED', message: 'Required.' }],
        });
      }
      const body = rawBody as TestSendRequest;
      const version =
        [...template.versions].reverse().find((v) => v.status === 'Approved') ??
        template.versions[0];
      if (!version) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      const address = 'address' in body.recipient ? body.recipient.address : me.user.email;
      const [, domain] = address.split('@');
      const digest = await sha256Hex(address);
      ntf.testSends.push({
        id: nextId('testsend'),
        templateId: template.id,
        version: version.version,
        locale: body.locale,
        recipientRef: `sha256:${digest}@${domain ?? ''}`,
        sentAt: new Date().toISOString(),
      });
      return jsonResponse({ sent: true }, 202);
    }
    return null;
  } catch (error) {
    if (error instanceof FakeApiError) {
      return new Response(
        JSON.stringify({
          status: error.status,
          errorCode: error.errorCode,
          title: error.errorCode,
          correlationId: correlationId ?? undefined,
          ...error.extra,
        }),
        { status: error.status, headers: { 'content-type': 'application/problem+json' } },
      );
    }
    throw error;
  }
}

function problemNotFound(correlationId: string | null): Response {
  return new Response(
    JSON.stringify({
      status: 404,
      errorCode: 'PLT_NOT_FOUND',
      title: 'PLT_NOT_FOUND',
      correlationId: correlationId ?? undefined,
    }),
    { status: 404, headers: { 'content-type': 'application/problem+json' } },
  );
}

async function sha256Hex(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
